import { useRef, useState } from 'react';
import type { DemoKey, DemoScript, Fx, Level, Pt, RoundProps, ViewProps } from '../kit.js';
import { BOARD, BoardFrame, clamp, easier, q, say, seeded, thirtyGame, useRound, useSvgId } from '../kit.js';
import { ERA_SKIN } from '../../art.js';
import type { Era } from '../../art.js';
import { SCATTER, ShipTop } from './art.js';

/**
 * NAVIGATOR · ASTEROID FIELD — slide the ship left and right past the rocks drifting down.
 *
 * The ship sits near the bottom and follows the finger across; anywhere on the board will do. Hold
 * on for ten or eleven seconds and the field is behind you. A rock that hits costs a mistake and
 * breaks up on the shield, which then fades out once and keeps the ship safe for a moment — the
 * flight goes on. Round 1 has a few slow rocks; each round after, more of them, a little faster,
 * more of them aimed at the ship and drifting a little sideways. Nothing comes until the first
 * touch. By era: space junk and rocks, city debris, torn hull plates, boulders, mossy stones.
 */

export interface RocksLevel {
  /** How long to hold on. */
  seconds: number;
  /** A rock every so many seconds (± 15%). */
  every: number;
  /** How fast they fall, units a second. */
  fall: readonly [number, number];
  /** Their radius, units. */
  size: readonly [number, number];
  /** How often a rock is aimed at the ship rather than anywhere. */
  aim: number;
  /** Most sideways drift, units a second. */
  sway: number;
}

/** Easier since 6 Oct 2026 (kit `easier`): round 1 as before, round 2 halfway to the old round 2, round 3 the old round 2. */
export const ROCKS_LEVELS: readonly RocksLevel[] = easier([
  { seconds: 10, every: 0.9, fall: [95, 120], size: [15, 22], aim: 0.35, sway: 0 },
  { seconds: 11, every: 0.7, fall: [112, 140], size: [14, 22], aim: 0.45, sway: 12 },
]);

const W = BOARD.w;
const H = BOARD.h;
/** The ship's centre, from the top, and how near a rock's edge may come. */
export const SHIP_Y = H - 46;
export const SHIP_R = 12;
/** After a hit, the shield holds this long. */
const SHIELD_MS = 1_100;
/** A rock broken on the shield scatters for this long. */
const BREAK_MS = 480;

export interface Rock {
  id: number;
  x: number;
  y: number;
  r: number;
  vy: number;
  vx: number;
  rot: number;
  spin: number;
  hitAt: number | null;
}

/** A lumpy outline of radius about `r`. */
export function rockShape(rand: () => number, r: number, corners = 9, rough = 0.24): string {
  const pts: string[] = [];
  for (let k = 0; k < corners; k++) {
    const a = (k / corners) * Math.PI * 2 + (rand() - 0.5) * 0.3;
    const d = r * (1 - rough / 2 + rand() * rough);
    pts.push(`${(Math.cos(a) * d).toFixed(1)} ${(Math.sin(a) * d).toFixed(1)}`);
  }
  return `M${pts.join(' L')} Z`;
}

/** A new rock at the top of a board `width` wide: now and then aimed at the ship, otherwise anywhere. */
export function spawnRock(rand: () => number, level: Level, width: number, shipX: number, id: number): Rock {
  const L = ROCKS_LEVELS[level]!;
  const r = L.size[0] + rand() * (L.size[1] - L.size[0]);
  const aimed = rand() < L.aim;
  const x = clamp(aimed ? shipX + (rand() - 0.5) * 60 : r + rand() * (width - 2 * r), r + 4, width - r - 4);
  return {
    id,
    x,
    y: -r - 6,
    r,
    vy: L.fall[0] + rand() * (L.fall[1] - L.fall[0]),
    vx: (rand() - 0.5) * 2 * L.sway,
    rot: rand() * 360,
    spin: (rand() - 0.5) * 70,
    hitAt: null,
  };
}

/** Does this rock touch the ship at (sx, sy)? A little forgiving: the rock's rim and the ship's wingtips may brush. */
export function rockHits(rock: Pick<Rock, 'x' | 'y' | 'r'>, sx: number, sy: number): boolean {
  return Math.hypot(rock.x - sx, rock.y - sy) < rock.r * 0.8 + SHIP_R;
}

/** One frame of a falling rock: it drifts, spins, and bounces off the sides. */
export function stepRock(rock: Rock, dt: number, width: number): void {
  rock.y += rock.vy * dt;
  rock.x += rock.vx * dt;
  rock.rot += rock.spin * dt;
  if (rock.x < rock.r || rock.x > width - rock.r) {
    rock.vx = -rock.vx;
    rock.x = clamp(rock.x, rock.r, width - rock.r);
  }
}

export type RockKind = 'rock' | 'junk' | 'plate';

/** How a rock looks, worked out from its number alone, so any screen draws the same rock. */
export interface RockLook {
  kind: RockKind;
  /** Its outline, around (0, 0). */
  shape: string;
  craters: readonly { x: number; y: number; r: number }[];
}

const looks = new Map<string, RockLook>();
export function rockLook(id: number, r: number, era: Era): RockLook {
  const key = `${era}:${id}:${r}`;
  const known = looks.get(key);
  if (known) return known;
  const rand = seeded(id * 7919 + 13);
  const pick = rand();
  const kind: RockKind = era === 'stars' && pick < 0.3 ? 'junk' : era === 'debris' && pick < 0.45 ? 'plate' : 'rock';
  const look = {
    kind,
    shape: kind === 'plate' ? rockShape(rand, r, 5, 0.5) : rockShape(rand, r),
    craters: [
      { x: (rand() - 0.5) * r * 0.8, y: (rand() - 0.5) * r * 0.8, r: r * (0.16 + rand() * 0.1) },
      { x: (rand() - 0.5) * r * 0.9, y: (rand() - 0.5) * r * 0.9, r: r * (0.1 + rand() * 0.08) },
    ],
  };
  if (looks.size > 300) looks.clear();
  looks.set(key, look);
  return look;
}

/** Everything the field shows. */
export interface RocksView {
  /** The game clock, ms. */
  t: number;
  /** The ship: across (units), its bank (degrees), the shield after a hit (0–1). */
  x: number;
  bank: number;
  sh: number;
  /** The rocks: across and down (units), radius, turn (degrees), number (its look), and ms since it broke on the shield (−1: whole). */
  k: readonly (readonly [x: number, y: number, r: number, rot: number, id: number, broke: number])[];
  /** How far through the field (0–1), and the whole seconds left. */
  e: number;
  left: number;
  /** How far the field has streamed past, seconds of flight. */
  f: number;
  /** 0 waiting for the first touch, 1 flying, 2 through the field (the ship climbs away). */
  s: 0 | 1 | 2;
  /** Through the field: how far the ship has climbed away (0–1). */
  out: number;
  fx?: Fx | undefined;
}

const TONES: Record<Era, { light: string; dark: string }> = {
  stars: { light: '#a3afbd', dark: '#252c36' },
  neon: { light: '#8a78a0', dark: '#1a1026' },
  debris: { light: '#735250', dark: '#1a0d0d' },
  dust: { light: '#cf9a5e', dark: '#3a220c' },
  canopy: { light: '#8f957c', dark: '#232a1c' },
};

function RockArt({ x, y, r, rot, look, era, id }: { x: number; y: number; r: number; rot: number; look: RockLook; era: Era; id: string }) {
  const skin = ERA_SKIN[era];
  if (look.kind === 'junk') {
    return (
      <g transform={`translate(${x} ${y}) rotate(${rot})`}>
        <rect x={-r * 1.15} y={-r * 0.28} width={r * 0.7} height={r * 0.56} fill="#1b3f7a" stroke="#6fa8e8" strokeWidth=".8" />
        <rect x={r * 0.45} y={-r * 0.28} width={r * 0.7} height={r * 0.56} fill="#1b3f7a" stroke="#6fa8e8" strokeWidth=".8" />
        <path d={`M${-r * 0.8} ${-r * 0.28} L${-r * 0.8} ${r * 0.28} M${r * 0.8} ${-r * 0.28} L${r * 0.8} ${r * 0.28}`} stroke="#6fa8e8" strokeWidth=".6" />
        <rect x={-r * 0.45} y={-r * 0.45} width={r * 0.9} height={r * 0.9} rx="2" fill={`url(#${id}gold)`} stroke="#5a3d10" strokeWidth=".8" />
        <path d={`M0 ${-r * 0.45} L${r * 0.2} ${-r * 0.95}`} stroke="#c9d4de" strokeWidth="1.2" />
        <circle cx={r * 0.2} cy={-r * 0.95} r="1.8" fill="#c9d4de" />
      </g>
    );
  }
  if (look.kind === 'plate') {
    return (
      <g transform={`translate(${x} ${y}) rotate(${rot})`}>
        <path d={look.shape} fill={`url(#${id}plate)`} stroke={skin.haze} strokeWidth="1.6" strokeOpacity=".85" />
        <path d={`M${-r * 0.6} ${-r * 0.1} L${r * 0.6} ${-r * 0.1}`} stroke="#e8b23a" strokeWidth={r * 0.28} strokeDasharray="4 4" opacity=".7" />
        <circle cx={-r * 0.35} cy={r * 0.35} r="1.3" fill="#000" opacity=".5" />
        <circle cx={r * 0.3} cy={r * 0.35} r="1.3" fill="#000" opacity=".5" />
      </g>
    );
  }
  return (
    <g transform={`translate(${x} ${y})`}>
      <g transform={`rotate(${rot})`}>
        <path d={look.shape} fill={`url(#${id}rock)`} stroke={skin.haze} strokeOpacity=".45" strokeWidth="1.2" />
        {look.craters.map((c, k) => (
          <g key={k}>
            <circle cx={c.x} cy={c.y} r={c.r} fill="#000" opacity=".28" />
            <path d={`M${c.x - c.r} ${c.y} A ${c.r} ${c.r} 0 0 0 ${c.x + c.r} ${c.y}`} stroke="#ffffff" strokeOpacity=".18" fill="none" />
          </g>
        ))}
        {era === 'canopy' && <ellipse cx={-r * 0.2} cy={-r * 0.45} rx={r * 0.55} ry={r * 0.3} fill="#5c9a44" opacity=".75" />}
      </g>
      {/* the light stays from above as it turns */}
      <ellipse cx={-r * 0.3} cy={-r * 0.38} rx={r * 0.38} ry={r * 0.22} fill="#ffffff" opacity=".16" />
    </g>
  );
}

/** The field, from its view state alone. */
function View({ view, era, accent, lang, input }: ViewProps<RocksView>) {
  const skin = ERA_SKIN[era];
  const tone = TONES[era];
  const id = useSvgId();
  // A demo glides every number between its keys: the state is read rounded.
  const state = Math.round(view.s);
  const sy = SHIP_Y - view.out * view.out * (SHIP_Y + 40);
  const status =
    state === 2
      ? { text: say(lang, '✓ THROUGH THE FIELD', '✓ GJENNOM FELTET'), tone: 'ok' as const }
      : view.sh > 0.6
        ? { text: say(lang, 'HIT — KEEP GOING', 'TRUFFET – FORTSETT'), tone: 'bad' as const }
        : state === 0
          ? { text: say(lang, 'HOLD ON', 'HOLD UT'), hint: say(lang, 'TOUCH AND SLIDE', 'TRYKK OG DRA') }
          : { text: say(lang, `HOLD ON · ${Math.round(view.left)} S`, `HOLD UT · ${Math.round(view.left)} S`), hint: say(lang, 'SLIDE PAST THE ROCKS', 'DRA FORBI STEINENE') };

  return (
    <BoardFrame era={era} accent={accent} status={status} fx={view.fx} input={input}>
      <defs>
        <radialGradient id={`${id}rock`} cx=".35" cy=".3" r=".8">
          <stop offset="0" stopColor={tone.light} />
          <stop offset="1" stopColor={tone.dark} />
        </radialGradient>
        <linearGradient id={`${id}plate`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#7a5654" />
          <stop offset="1" stopColor="#1f0f10" />
        </linearGradient>
        <linearGradient id={`${id}gold`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ffe08a" />
          <stop offset=".5" stopColor="#c9962f" />
          <stop offset="1" stopColor="#6b4a12" />
        </linearGradient>
      </defs>

      {/* the field streams past */}
      {SCATTER.slice(0, 14).map((p, i) => {
        const v = 45 + (i % 4) * 32;
        const y = ((p.y * (H + 40) + view.f * v) % (H + 40)) - 20;
        return <line key={i} x1={p.x * W} y1={y} x2={p.x * W} y2={y + v / 14} stroke={skin.haze} strokeOpacity={0.25 + (i % 3) * 0.12} strokeWidth={p.r} strokeLinecap="round" />;
      })}
      {era === 'stars' && <circle cx={W * 0.1} cy={H + 330} r={360} fill="#0b2a55" opacity=".5" stroke={skin.haze} strokeOpacity=".3" />}

      {view.k.map(([x, y, r, rot, rid, broke]) =>
        broke < 0 ? (
          <RockArt key={rid} x={x} y={y} r={r} rot={rot} look={rockLook(rid, r, era)} era={era} id={id} />
        ) : (
          <g key={rid} transform={`translate(${x} ${y})`} opacity={1 - broke / BREAK_MS}>
            {[0, 1, 2, 3, 4].map((k) => {
              const a = (k / 5) * Math.PI * 2 + rot / 57;
              const d = 6 + (broke / BREAK_MS) * 34;
              return (
                <path
                  key={k}
                  d={`M0 -${r * 0.35} L${r * 0.3} ${r * 0.2} L-${r * 0.3} ${r * 0.2} Z`}
                  transform={`translate(${Math.cos(a) * d} ${Math.sin(a) * d}) rotate(${k * 70 + broke / 4})`}
                  fill={tone.light}
                />
              );
            })}
          </g>
        ),
      )}

      <g transform={`translate(${view.x} ${sy})`}>
        <ShipTop era={era} burn={state === 2 ? 1 : 0.45 + 0.1 * Math.sin(view.f * 9)} bank={view.bank} shield={view.sh} />
      </g>
      {state === 0 && (
        <g transform={`translate(${W / 2 + Math.sin(view.t / 255) * 8} ${SHIP_Y + 30})`}>
          <path d="M-26 0 L26 0 M-26 0 l7 -5 M-26 0 l7 5 M26 0 l-7 -5 M26 0 l-7 5" stroke={accent} strokeWidth="2.2" strokeLinecap="round" fill="none" />
        </g>
      )}

      {/* how far through the field */}
      <rect x="14" y="10" width={W - 28} height="4" rx="2" fill="#ffffff" opacity=".1" />
      <rect x="14" y="10" width={(W - 28) * clamp(view.e, 0, 1)} height="4" rx="2" fill={accent} />
    </BoardFrame>
  );
}

function Board(props: RoundProps) {
  const { level, lang, era } = props;
  const L = ROCKS_LEVELS[level]!;
  const [rand] = useState(() => seeded(Date.now() + level * 15485863));
  const rocks = useRef<Rock[]>([]);
  const nextId = useRef(Math.floor(rand() * 1000));
  const shipX = useRef(W / 2);
  const target = useRef<number | null>(null);
  const started = useRef(false);
  const elapsed = useRef(0);
  const ambient = useRef(0);
  const spawnIn = useRef(0.2);
  const shieldAt = useRef(-1e9);

  const round = useRound(props, (dt, now, won) => {
    ambient.current += dt * (started.current ? 1 : 0.35);
    // The ship follows the finger, quickly but never in a jump.
    const to = target.current ?? shipX.current;
    shipX.current = clamp(shipX.current + (to - shipX.current) * Math.min(1, dt * 12), 22, W - 22);
    if (!started.current) return;
    for (const rock of rocks.current) if (rock.hitAt === null) stepRock(rock, dt, W);
    rocks.current = rocks.current.filter((r) => r.y < H + 40 && (r.hitAt === null || now - r.hitAt < BREAK_MS));
    if (won) return;
    elapsed.current += dt;
    spawnIn.current -= dt;
    if (spawnIn.current <= 0 && elapsed.current < L.seconds - 1.2) {
      rocks.current.push(spawnRock(rand, level, W, shipX.current, nextId.current++));
      spawnIn.current = L.every * (0.85 + rand() * 0.3);
    }
    if (now - shieldAt.current > SHIELD_MS) {
      for (const rock of rocks.current) {
        if (rock.hitAt === null && rockHits(rock, shipX.current, SHIP_Y)) {
          rock.hitAt = now;
          shieldAt.current = now;
          round.miss();
          break;
        }
      }
    }
    round.progress(Math.min(0.99, Math.floor((elapsed.current / L.seconds) * 20) / 20));
    if (elapsed.current >= L.seconds) round.win();
  });

  const aim = (p: Pt) => {
    if (!round.live) return;
    started.current = true;
    target.current = clamp(p.x, 22, W - 22);
  };

  const now = round.now;
  const sx = shipX.current;
  const view: RocksView = {
    t: q(now),
    x: q(sx),
    bank: q(clamp(((target.current ?? sx) - sx) * 0.35, -22, 22)),
    sh: q(clamp(1 - (now - shieldAt.current) / SHIELD_MS, 0, 1), 2),
    k: rocks.current.map((r) => [q(r.x), q(r.y), q(r.r), q(((r.rot % 360) + 360) % 360), r.id, r.hitAt === null ? -1 : q(now - r.hitAt)] as const),
    e: q(clamp(elapsed.current / L.seconds, 0, 1), 3),
    left: Math.max(0, Math.ceil(L.seconds - elapsed.current)),
    f: q(ambient.current, 2),
    s: round.won ? 2 : started.current ? 1 : 0,
    out: q(round.clear, 2),
    fx: round.fx,
  };
  return (
    <View
      view={round.show(view)}
      era={era}
      accent={props.accent}
      lang={lang}
      input={{ label: say(lang, 'Slide to steer', 'Dra for å styre'), down: aim, move: aim }}
    />
  );
}

/** The how-to's three rocks: across (units), when each comes in (ms), and its number. */
const DEMO_ROCKS = [
  [160, 600, 11],
  [70, 1_800, 12],
  [250, 3_000, 15],
] as const;
/** Where the finger (and the ship after it) is across the board, at moments of the loop. */
const DEMO_PATH = [
  [0, 160],
  [1_600, 160],
  [2_200, 92],
  [3_400, 92],
  [4_000, 226],
  [4_600, 226],
  [5_200, 150],
] as const;
const DEMO_FALL = 0.11;
const demoX = (ms: number) => {
  let x: number = DEMO_PATH[0][1];
  for (let i = 1; i < DEMO_PATH.length; i++) {
    const [t0, x0] = DEMO_PATH[i - 1]!;
    const [t1, x1] = DEMO_PATH[i]!;
    if (ms >= t1) x = x1;
    else if (ms > t0) x = x0 + ((x1 - x0) * (ms - t0)) / (t1 - t0);
  }
  return x;
};

/**
 * The how-to: the finger touches under the ship and slides it left, right and back as three rocks
 * fall, and every rock goes by. Worked out once and keyed every tenth of a second.
 */
function demoField(): DemoScript<RocksView> {
  const ms = 7_000;
  const keys: DemoKey<RocksView>[] = [];
  for (let t = 0; t <= ms; t += 100) {
    const x = demoX(t);
    keys.push({
      at: t,
      view: {
        t: 0,
        x: q(x),
        bank: q(clamp((demoX(t + 120) - x) * 0.35, -22, 22)),
        sh: 0,
        k: DEMO_ROCKS.map(([rx, from, id], i) => [rx, q(clamp(-30 + (t - from) * DEMO_FALL, -30, H + 30)), 18, q(t * 0.04 + i * 50), id, -1] as const),
        e: q(Math.max(0, t - 600) / 10_000, 3),
        left: Math.ceil(10 - Math.max(0, t - 600) / 1000),
        f: q(t < 600 ? (t / 1000) * 0.35 : 0.21 + (t - 600) / 1000, 2),
        s: t < 600 ? 0 : 1,
        out: 0,
      },
    });
  }
  return {
    ms,
    keys,
    finger: [
      { at: 0, x: 160, y: 304, hide: true },
      { at: 300, x: 160, y: 304, hide: false },
      { at: 600, x: 160, y: 304, down: true },
      ...DEMO_PATH.slice(1).map(([at, x]) => ({ at, x, y: 304, ease: 'linear' as const })),
      { at: 6_200, x: 150, y: 304, down: false },
      { at: 6_600, x: 170, y: 312, hide: true },
    ],
    steps: [
      { at: 0, en: 'TOUCH AND HOLD', no: 'TRYKK OG HOLD' },
      { at: 1_400, en: 'SLIDE — THE SHIP FOLLOWS', no: 'DRA – SKIPET FØLGER' },
      { at: 3_200, en: 'MISS THE ROCKS', no: 'UNNGÅ STEINENE' },
    ],
  };
}

export const game = thirtyGame<RocksView>({
  Board,
  View,
  demo: demoField(),
  words: {
    en: {
      instruction: 'Slide the ship left and right past the rocks. Hold on until the field is behind you.',
      howMove: 'Keep a finger on the board and slide it. The ship follows.',
    },
    no: {
      instruction: 'Dra skipet til sidene forbi steinene. Hold ut til feltet er bak deg.',
      howMove: 'Hold fingeren på brettet og dra. Skipet følger etter.',
    },
  },
});
